import { useMutation, useQueryClient } from '@tanstack/react-query';
import QualAPI from 'utils/QualAPI';
import SubscriptionAPI from 'apis/SubscriptionAPI';
import { subscriptionKeys } from './queries';

/**
 * Hook to cancel a subscription
 * @returns {Object} Mutation object with mutate function
 * 
 * Response payload format:
 * {
 *   "id": 5,
 *   "user_id": 10,
 *   "current_period_end": "2025-09-01T18:17:30.000Z",
 *   "status": "trialing",
 *   "cancel_at_period_end": true,
 *   "quantity": 1,
 *   "plan": {
 *     "id": 2,
 *     "amount": "5000.0",
 *     "interval": "month"
 *   }
 * }
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (subscriptionId) => QualAPI.cancelSubscription(subscriptionId).then(response => response.data),
    onSuccess: (updatedSubscription) => {
      // Update the cache directly with the response data
      // The subscriptions endpoint returns an array, so wrap the updated subscription
      queryClient.setQueryData(subscriptionKeys.all, {
        subscriptions: [updatedSubscription]
      });
    }
  });
}

/**
 * Hook to renew/uncancel a subscription
 * @returns {Object} Mutation object with mutate function
 * 
 * Response payload format:
 * {
 *   "id": 5,
 *   "user_id": 10,
 *   "current_period_end": "2025-09-01T18:17:30.000Z",
 *   "status": "trialing",
 *   "cancel_at_period_end": false,  // This will be false after renewal
 *   "quantity": 1,
 *   "plan": {
 *     "id": 2,
 *     "amount": "5000.0",
 *     "interval": "month"
 *   }
 * }
 */
export function useRenewSubscription() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (subscriptionId) => QualAPI.renewSubscription(subscriptionId).then(response => response.data),
    onSuccess: (updatedSubscription) => {
      // Update the cache directly with the response data
      // The subscriptions endpoint returns an array, so wrap the updated subscription
      queryClient.setQueryData(subscriptionKeys.all, {
        subscriptions: [updatedSubscription]
      });
    }
  });
}

/**
 * Hook to add a seat to a subscription
 * @returns {Object} Mutation object with mutate function
 */
export function useAddSeat() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ subscriptionId, email }) => QualAPI.addSeat(subscriptionId, email).then(response => response.data),
    onSuccess: (_, { subscriptionId }) => {
      // Invalidate both subscriptions and seats
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.seats(subscriptionId) });
    }
  });
}

/**
 * Hook to remove a seat from a subscription
 * @returns {Object} Mutation object with mutate function
 */
export function useRemoveSeat() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ subscriptionId, seatId }) => QualAPI.removeSeat(subscriptionId, seatId).then(response => response.data),
    onSuccess: (_, { subscriptionId }) => {
      // Invalidate both subscriptions and seats
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.seats(subscriptionId) });
    }
  });
}

/**
 * Hook to end a trial period for a subscription
 * @returns {Object} Mutation object with mutate function
 */
export function useEndTrial() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (subscriptionId) => SubscriptionAPI.endTrial(subscriptionId).then(response => response.data),
    onSuccess: (updatedSubscription) => {
      // Update the cache directly with the response data
      // The subscriptions endpoint returns an array, so wrap the updated subscription
      queryClient.setQueryData(subscriptionKeys.all, {
        subscriptions: [updatedSubscription]
      });
    },
    onError: (error) => {
      console.error('End trial failed:', error);
    }
  });
}